import { useMutation } from '@tanstack/react-query';
import { toast } from '@huspy/forge/shared';
import queryClient from '@app/queryClient';
import { bankApplicationsApi } from '@modules/opportunities/entities/bankApplication/api';
import { t } from 'i18next';
import { trackAmplitudeEvent } from '@shared/analytics/amplitude';
import { USER_EVENTS } from '@shared/analytics/events';
import { BankApplication } from '@modules/opportunities/entities/bankApplication/bankApplication.entity';
import { BANK_APPLICATION_STATUS } from '@modules/opportunities/entities/bankApplication/const';
import { SubmitToBankBodyDTO } from '../api/dto/submitToBank.dto';

// eslint-disable-next-line max-len
const baseTranslation = 'opportunity.caseSubmission.submitApplications.bankAndHuspySubmission.directToBankSlido.notifications';

const useSubmitToBank = (
  opportunityExternalId: string,
  bankApplicationExternalId: string,
  bankApplications?: BankApplication[]
) => {
  const {
    error, mutate, isPending, mutateAsync,
  } = useMutation({
    mutationKey: ['useSubmitToBank'],
    mutationFn: (requestBody: SubmitToBankBodyDTO) => {
      // Track attempt
      const submittedBankApplications = bankApplications
        ?.filter((bankApplication) => bankApplication.status !== BANK_APPLICATION_STATUS.draft) || [];

      trackAmplitudeEvent(USER_EVENTS.CASE.CASE_SUBMISSION_ATTEMPTED, {
        useContext: true,
        addToContext: true,
        case_id: opportunityExternalId,
        banks: undefined,
        destination_type: 'Bank',
        destinations: [bankApplicationExternalId],
        application_rank: submittedBankApplications.length + 1,
      });

      return bankApplicationsApi.submitToBank({
        opportunityExternalId,
        bankApplicationExternalId,
        requestBody,
      });
    },
    onSuccess: () => {
      // Track success
      trackAmplitudeEvent(USER_EVENTS.CASE.CASE_SUBMISSION_COMPLETED, { useContext: true });
      queryClient.refetchQueries({ queryKey: ['clientOpportunity', opportunityExternalId] });
      toast('info', { message: t(`${baseTranslation}.success`) });
    },
    onError: (err) => {
      // Track error
      trackAmplitudeEvent(USER_EVENTS.CASE.CASE_SUBMISSION_FAILED, {
        useContext: true,
        error_message: err.message ?? '',
      });

      toast('error', { message: t(`${baseTranslation}.failed`) });
    },
  });

  return {
    mutate,
    error,
    isPending,
    mutateAsync,
  };
};

export default useSubmitToBank;
